import React, { useEffect, useState } from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  FormControl,
  Select,
  InputLabel,
  SelectChangeEvent,
  MenuItem,
  TextField,
  Autocomplete,
} from "@mui/material";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";
import { useLogger } from "../../../../contexts/LoggerContext";
import { Penalty, RosterPlayer } from "../../../../types/LogTypes";
import { useDepthChart } from "../../../../contexts/DepthChartContext";

interface PenaltyLogProps {
  players: RosterPlayer[];
}

export const PenaltyLog: React.FC<PenaltyLogProps> = ({ players }) => {
  const { penalties, selectedGame, setCurrentPlayPenalty, clearSelections } =
    useLogger();

  const [penaltyAccepted, setPenaltyAccepted] = useState(true);
  const [selectedPenalty, setSelectedPenalty] = useState<Penalty | null>(null);
  const [penaltyUserClub, setPenaltyUserClub] = useState<boolean>(true);
  const [selectedPlayer, setSelectedPlayer] = useState<RosterPlayer | null>(
    null
  );
  const [jerseyNumber, setJerseyNumber] = useState<string>("");

  const findPlayer = (jerseyNumber: string) => {
    return players.find(
      (player) => player.jersey_number?.toString() === jerseyNumber
    );
  };

  const handleJerseyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setJerseyNumber(value);
      setSelectedPlayer(findPlayer(value) || null);
    }
  };

  useEffect(() => {
    if (clearSelections) {
      setSelectedPlayer(null);
      setSelectedPenalty(null);
      setPenaltyAccepted(true);
      setPenaltyUserClub(true);
    }
  }, [clearSelections]);

  const clubLogo = selectedGame?.user_club?.profile_picture;
  const oppClubLogo = selectedGame?.opponent_club?.profile_picture;
  const clubName = selectedGame?.user_club?.name;
  const oppClubName = selectedGame?.opponent_club?.name;

  const handlePenaltyAccepted = () => {
    setPenaltyAccepted(!penaltyAccepted);
  };

  const handlePenaltyChange = (event: SelectChangeEvent<number | "">) => {
    const penaltyId = event.target.value;
    const penalty = penalties.find((p: Penalty) => p.id === penaltyId);
    if (penalty) {
      setSelectedPenalty(penalty);
    }
  };

  const handlePlayerChange = (event: SelectChangeEvent<number | "">) => {
    const playerId = event.target.value;
    const player = players.find((p: RosterPlayer) => p.id === playerId);
    if (player) {
      setSelectedPlayer(player);
    }
  };

  const handleTeamSelection = () => {
    setPenaltyUserClub(!penaltyUserClub);
  };

  useEffect(() => {
    if (
      selectedPenalty &&
      selectedGame?.user_club &&
      selectedGame?.opponent_club &&
      selectedPlayer
    ) {
      setCurrentPlayPenalty({
        penalty: selectedPenalty,
        accepted: penaltyAccepted,
        club: penaltyUserClub
          ? selectedGame?.user_club
          : selectedGame?.opponent_club,
        yardage: 0,
        roster_player: selectedPlayer,
      });
    }
  }, [selectedPenalty, penaltyAccepted, penaltyUserClub, selectedPlayer]);

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Team */}
        <Grid item xs={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <RadioGroup
              value={penaltyUserClub ? true : false}
              onChange={handleTeamSelection}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Box
                  component="img"
                  src={clubLogo}
                  alt={clubName}
                  sx={{
                    height: 40,
                    width: 40,
                    objectFit: "contain",
                    borderRadius: "4px",
                    ml: { xs: 0, sm: 0.5 },
                    mr: 0.5,
                  }}
                />
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label={clubName}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Box
                  component="img"
                  src={oppClubLogo}
                  alt={oppClubName}
                  sx={{
                    height: 40,
                    width: 40,
                    objectFit: "contain",
                    borderRadius: "4px",
                    ml: { xs: 0, sm: 0.5 },
                    mr: 0.5,
                  }}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label={oppClubName}
                />
              </Box>
            </RadioGroup>
          </Box>
        </Grid>
        {/* Penalty & PlayerSelect */}
        <Grid item xs={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 250 }}>
              <InputLabel id="penalty-label">Penalty</InputLabel>
              <Autocomplete
                options={penalties}
                getOptionLabel={(option) => option.name}
                value={selectedPenalty}
                onChange={(_, newValue) => setSelectedPenalty(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Penalty" variant="outlined" />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 250 }}>
              <InputLabel id="player-label">Player</InputLabel>
              <Select
                labelId="player-label"
                id="player"
                value={selectedPlayer?.id || ""}
                label="Player"
                onChange={handlePlayerChange}
              >
                {players.map((player: RosterPlayer) => (
                  <MenuItem key={player.id} value={player.id}>
                    {jerseyNumberNameDisplay(player)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="Jersey Number"
              value={jerseyNumber}
              fullWidth
              onChange={handleJerseyNumberChange}
              inputProps={{
                min: 0,
                max: 99,
                step: 1,
              }}
            />
          </Box>
        </Grid>
        {/* Acceptance */}
        <Grid item xs={4}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <RadioGroup
              value={penaltyAccepted ? true : false}
              onChange={handlePenaltyAccepted}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Accepted"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="Declined"
              />
            </RadioGroup>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
